/** Human labels for entries, shared by lists and the detail sheet. */
import { type AccountKind, type Entry, type Master, type Paisa } from '@khata/core';
import { dShort, num, qty, taka } from './format.js';
import { t } from './i18n.js';
import type { StringKey } from './strings.js';

export function partyName(master: Master, id: string | null, walkIn = t('walkIn')): string {
  if (id === null) return walkIn;
  return master.parties.get(id)?.name ?? t('label_deletedParty');
}

export function itemName(master: Master, id: string): string {
  return master.items.get(id)?.name ?? t('label_deletedItem');
}

export function accountName(master: Master, id: string): string {
  return master.accounts.get(id)?.name ?? t('label_closedAccount');
}

export function accountKind(kind: AccountKind): string {
  return kind === 'cash'
    ? t('bank_kindCash')
    : kind === 'bank'
      ? t('bank_kindBank')
      : t('bank_kindMobile');
}

export function linesSummary(
  master: Master,
  e: Extract<Entry, { type: 'sale' | 'purchase' }>,
): string {
  return e.lines.map((l) => `${itemName(master, l.itemId)} ×${qty(l.qty)}`).join(', ');
}

export interface EntryLabel {
  title: string;
  sub: string;
  amount: string;
  tone: 'good' | 'bad' | 'ink' | 'muted';
}

export const returnReason = (r: string): string => t(`reason_${r}` as StringKey);
export const adjustReason = (r: string): string => t(`adjustReason_${r}` as StringKey);
export const typeLabel = (type: Entry['type']): string => t(`type_${type}` as StringKey);

function itemsWord(n: number): string {
  return `${num(n)} ${t(n === 1 ? 'item' : 'items')}`;
}

export function describe(master: Master, e: Entry): EntryLabel {
  const d = dShort(e.date);
  switch (e.type) {
    case 'sale': {
      const due = e.total - e.paid;
      return {
        title: `${t('type_sale')} · ${partyName(master, e.partyId)}`,
        sub: `${d} · ${itemsWord(e.lines.length)}${due > 0 ? ` · ${t('bokeya')} ` + taka(due as Paisa) : ''}`,
        amount: '+' + taka(e.total),
        tone: 'good',
      };
    }
    case 'purchase':
      return {
        title: `${t('type_purchase')} · ${partyName(master, e.partyId, t('noVendor'))}`,
        sub: `${d} · ${itemsWord(e.lines.length)}`,
        amount: '−' + taka(e.total),
        tone: 'ink',
      };
    case 'payment':
      return {
        title: `${t(e.direction === 'in' ? 'label_receivedFrom' : 'label_paidTo')} ${partyName(master, e.partyId)}`,
        sub: `${d} · ${accountName(master, e.accountId)}${e.note ? ' · ' + e.note : ''}`,
        amount: (e.direction === 'in' ? '+' : '−') + taka(e.amount),
        tone: e.direction === 'in' ? 'good' : 'ink',
      };
    case 'return':
      return {
        title: `${t('type_return')} · ${itemName(master, e.itemId)} ×${qty(e.qty)}`,
        sub: `${d} · ${e.kind === 'sale' ? `${t('returns_fromCustomer')} ${partyName(master, e.partyId)}` : `${t('returns_toVendor')} ${partyName(master, e.partyId, t('noVendor'))}`} · ${returnReason(e.reason)}`,
        amount: taka(e.amount),
        tone: 'bad',
      };
    case 'expense':
      return {
        title: `${t('type_expense')} · ${e.purpose}`,
        sub: `${d} · ${accountName(master, e.accountId)}${e.note ? ' · ' + e.note : ''}`,
        amount: '−' + taka(e.amount),
        tone: 'ink',
      };
    case 'drawing':
      return {
        title: `${t('type_drawing')} · ${e.who}`,
        sub: `${d} · ${accountName(master, e.accountId)}${e.note ? ' · ' + e.note : ''}`,
        amount: '−' + taka(e.amount),
        tone: 'ink',
      };
    case 'transfer': {
      const from = master.accounts.get(e.fromAccountId);
      const to = master.accounts.get(e.toAccountId);
      const title =
        from?.kind === 'cash'
          ? t('label_deposit', { to: accountName(master, e.toAccountId) })
          : to?.kind === 'cash'
            ? t('label_withdraw', { from: accountName(master, e.fromAccountId) })
            : `${accountName(master, e.fromAccountId)} → ${accountName(master, e.toAccountId)}`;
      return {
        title,
        sub: `${d}${e.note ? ' · ' + e.note : ''}`,
        amount: taka(e.amount),
        tone: to?.kind === 'cash' ? 'ink' : 'good',
      };
    }
    case 'stock_adjust':
      return {
        title: `${adjustReason(e.reason)} · ${itemName(master, e.itemId)} ${e.qty > 0 ? '+' : '−'}${qty(Math.abs(e.qty) as never)}`,
        sub: `${d} · ${e.bin === 'defective' ? t('defectiveBin') : t('stock')}${e.note ? ' · ' + e.note : ''}`,
        amount: e.unitCost !== null ? taka(e.unitCost) + t('label_perUnit') : '',
        tone: 'muted',
      };
    case 'account_opening':
      return {
        title: `${t('label_openingBalance')} · ${accountName(master, e.accountId)}`,
        sub: d,
        amount: taka(e.amount),
        tone: 'muted',
      };
    case 'party_opening': {
      const p = master.parties.get(e.partyId);
      const owes = p?.type === 'vendor' ? e.amount < 0 : e.amount > 0;
      return {
        title: `${t('label_openingBalance')} · ${partyName(master, e.partyId)}`,
        sub: `${d} · ${owes ? t(p?.type === 'vendor' ? 'parties_youOwe' : 'parties_owesYou') : t('parties_advance')}`,
        amount: taka(Math.abs(e.amount) as Paisa),
        tone: 'muted',
      };
    }
    case 'void':
      return {
        title: t('type_void'),
        sub: `${d}${e.reason ? ' · ' + e.reason : ''}`,
        amount: '',
        tone: 'muted',
      };
  }
}
